import { Component, createRef } from 'preact';

import { PackScene } from '../../utils/game/pack3d/packScene';
import { getLocalApiOrigin } from '../../utils/localApi';
import { chargeUp, burst as burstAudio, cardsOut, foilChime } from '../../utils/arena/packAudio';

/**
 * The pack, as a lit object rather than a picture of one.
 *
 * Mirrors `InitiativeDiceStage`: the canvas is created imperatively, the
 * renderer is torn down on unmount, and a failure to get a context leaves the
 * screen working without it. A pack that cannot be drawn must still open, or
 * the player has paid for cards they can never see.
 *
 * Props:
 *   setKey   : which set's print goes on the pack.
 *   hasFoil  : whether this pack holds a foil, for the extra note on the burst.
 *   onReady  : the model and its print are loaded; the pack can now be clicked.
 *   onCharge : the player has committed.
 *   onBurst  : the pack has gone off. Cue anything screen-wide off this.
 *   onCardsOut : the cards should start flying, while the debris is still up.
 *   onDone   : the sequence is spent; hand over to the reveal.
 *   onUnavailable : the scene cannot run at all. No WebGL, or the model or
 *     print failed to load. The caller is expected to play something else
 *     rather than send the player straight to the cards.
 */

/** From the burst to the cards being thrown out. */
const CARDS_AFTER_BURST_MS = 120;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default class BoosterPackScene extends Component {
  holderRef = createRef();
  timers = [];

  componentDidMount() {
    const holder = this.holderRef.current;
    if (!holder) return;
    this.reduced = prefersReducedMotion();

    this.canvas = document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.display = 'block';
    // A context lost after the pack is already up takes it off the screen
    // without stopping the loop or throwing. The player is then asked to
    // click a pack that is no longer drawn. Hand back instead.
    this.canvas.addEventListener('webglcontextlost', this.handleContextLost);
    holder.appendChild(this.canvas);

    try {
      this.gl = new PackScene(this.canvas);
    } catch (err) {
      // No WebGL (headless tests, blocked context).
      this.reportUnavailable(`WebGL unavailable: ${err?.message || err}`);
      return;
    }

    const origin = getLocalApiOrigin();
    this.measure();
    this.gl
      .load(
        `${origin}/game-assets/booster-pack.glb`,
        `${origin}/game-assets/booster-${this.props.setKey}-panel.webp`,
        `${origin}/game-assets/pack-cracks.png`,
      )
      .then(() => {
        if (!this._unmounted) this.props.onReady?.();
      })
      .catch((err) => {
        // Hand back rather than finishing. Calling onDone here was worse than
        // useless: a 404 on the model looked exactly like a pack that opens
        // with no animation at all, which is indistinguishable from the
        // feature not being wired up.
        if (!this._unmounted) {
          this.reportUnavailable(`could not load the pack model or print: ${err?.message || err}`);
        }
      });

    this.startLoop();
    window.addEventListener('resize', this.measure);
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(this.measure);
      this.ro.observe(this.canvas);
    }
  }

  handleContextLost = (e) => {
    e.preventDefault();
    this.reportUnavailable('the WebGL context was lost');
  };

  componentWillUnmount() {
    this._unmounted = true;
    this.canvas?.removeEventListener('webglcontextlost', this.handleContextLost);
    if (this.raf) cancelAnimationFrame(this.raf);
    for (const t of this.timers) clearTimeout(t);
    window.removeEventListener('resize', this.measure);
    if (this.ro) this.ro.disconnect();
    if (this.gl) this.gl.dispose();
    if (this.canvas?.parentNode) this.canvas.parentNode.removeChild(this.canvas);
  }

  measure = () => {
    if (!this.gl || !this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    this.gl.setSize(
      Math.max(1, Math.round(rect.width || 320)),
      Math.max(1, Math.round(rect.height || 420)),
    );
  };

  startLoop() {
    if (this.raf) return;
    let last = 0;
    const tick = (now) => {
      if (this._unmounted || !this.gl) return;
      const previous = last;
      last = now;
      // The first step is zero: the whole setup sits between the request and
      // this frame, and the pack would start mid-bob.
      const dt = previous === 0 ? 0 : Math.min(0.04, (now - previous) / 1000);
      // A throw here used to escape the rAF callback, so the loop stopped
      // scheduling itself and the pack froze mid-sequence with nothing
      // reported. The player sat in front of an empty room waiting for a
      // pack the server had already granted.
      let event;
      try {
        event = this.gl.render(dt, this.reduced);
      } catch (err) {
        this.reportUnavailable(`render failed: ${err?.message || err}`);
        return;
      }
      if (event === 'burst') this.handleBurst();
      else if (event === 'done') this.finish();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  handleBurst() {
    burstAudio();
    if (this.props.hasFoil) foilChime();
    this.props.onBurst?.();
    // The cards leave *with* the pack, not after it. Waiting for the debris to
    // clear and only then starting the fan reads as two unrelated animations
    // played back to back; overlapping them is what makes the cards look like
    // they came out of the pack.
    this.timers.push(setTimeout(() => {
      if (!this._unmounted) { cardsOut(); this.props.onCardsOut?.(); }
    }, CARDS_AFTER_BURST_MS));
  }

  /**
   * Give up on the 3D pack and say why.
   *
   * Parked on `window` as well as logged, because the only symptom the player
   * sees is the old flat animation, and "it fell back" and "it was never wired
   * up" look identical from the outside. This makes the reason one line to
   * retrieve instead of a hunt through the console.
   */
  reportUnavailable(reason) {
    console.error(`[BoosterPackScene] ${reason}`);
    if (typeof window !== 'undefined') window.__packSceneError = reason;
    this.props.onUnavailable?.(reason);
  }

  /** Hand over to the reveal. Single-shot. The loop keeps running. */
  finish() {
    if (this._finished) return;
    this._finished = true;
    this.props.onDone?.();
  }

  handlePointerMove = (e) => {
    if (!this.gl || this.reduced) return;
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    this.gl.setLean(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      ((e.clientY - rect.top) / rect.height) * 2 - 1,
    );
  };

  handlePointerLeave = () => this.gl?.setLean(0, 0);

  handleClick = () => {
    if (!this.gl) return;
    if (!this.gl.charge()) return;
    chargeUp();
    this.props.onCharge?.();
  };

  render() {
    return (
      <div
        ref={this.holderRef}
        onPointerMove={this.handlePointerMove}
        onPointerLeave={this.handlePointerLeave}
        onClick={this.handleClick}
        style={{ width: '100%', height: '100%', cursor: 'pointer' }}
      />
    );
  }
}
